import { useEffect, useRef, useState } from "react";
import { STATUS_FLOW, STATUS_LABEL, CATEGORY_LABEL, CHANNELS, CH_LABEL, CHANNEL_REVIEW_TAG } from "../constants.ts";
import { openDashMac } from "../utils/dashLinks.ts";
import { channelSelectOptions } from "../utils/ticketChannelEdit.ts";
import type { Channel, Ticket } from "../types.ts";
import type { MentionUser } from "../utils/messageDisplay.ts";
import { slaBadgeClass, slaState } from "../utils/sla.ts";
import { useMinuteTick } from "../utils/useMinuteTick.ts";
import { useUiState } from "../utils/uiState.ts";
import { metaSummaryLine } from "../utils/ticketDisplay.ts";

export function TicketHeader({
  ticket,
  displayTitle,
  ticketViewers,
  transitions,
  mentionUsers,
  canWrite,
  canEditChannel,
  moreActionsOpen,
  moreActionsRef,
  onGoToDevice,
  onStatusChange,
  onAssigneeChange,
  onChannelChange,
  onTitleChange,
  onMoreActionsToggle,
  onTriage,
  onCorrection,
  onJunkClose,
  onAssignSelf,
  railOpen,
  onRailToggle,
  hasOpenTasks,
  onBackToList,
}: {
  ticket: Ticket;
  displayTitle: string;
  ticketViewers: string[];
  transitions: Record<string, string[]>;
  mentionUsers: MentionUser[];
  canWrite: boolean;
  canEditChannel: boolean;
  moreActionsOpen: boolean;
  moreActionsRef: React.RefObject<HTMLDivElement | null>;
  onGoToDevice: (serial: string) => void;
  onStatusChange: (status: string) => void;
  onAssigneeChange: (email: string | null) => void;
  onChannelChange: (channel: Channel) => void;
  onTitleChange: (title: string) => void | Promise<void>;
  onMoreActionsToggle: () => void;
  onTriage: () => void;
  onCorrection: () => void;
  /** 迷惑メールとしてクローズ（status=closed + タグ「迷惑」+ 送信元ブロック） */
  onJunkClose?: () => void;
  /** 未割当のとき「自分が対応」で 1 クリック割当（改善 E） */
  onAssignSelf?: () => void;
  /** 情報レールの開閉（改善 B-1） */
  railOpen?: boolean;
  onRailToggle?: () => void;
  hasOpenTasks?: boolean;
  onBackToList?: () => void;
}) {
  // メタ情報は既定で1行サマリ。詳細チップ群はアコーディオン（改善 C）
  const [metaOpen, setMetaOpen] = useUiState<boolean>("meta.open", false);
  const now = useMinuteTick();
  const sla = slaState(ticket, now);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState("");
  const [titleSaving, setTitleSaving] = useState(false);
  const [titleError, setTitleError] = useState<string | null>(null);
  const titleInputRef = useRef<HTMLInputElement | null>(null);
  // blur で保存するとき Esc キャンセルと競合しないようフラグ
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    setEditingTitle(false);
    setTitleDraft("");
    setTitleError(null);
    setTitleSaving(false);
  }, [ticket.id]);

  useEffect(() => {
    if (editingTitle) titleInputRef.current?.focus();
  }, [editingTitle]);

  const startEditTitle = () => {
    if (!canWrite || titleSaving) return;
    setTitleDraft((ticket.title || "").trim());
    setTitleError(null);
    skipBlurSaveRef.current = false;
    setEditingTitle(true);
  };

  const cancelEditTitle = () => {
    skipBlurSaveRef.current = true;
    setEditingTitle(false);
    setTitleDraft("");
    setTitleError(null);
  };

  const commitEditTitle = async () => {
    if (titleSaving) return;
    const next = titleDraft.trim();
    if (!next) {
      setTitleError("件名は空にできません");
      titleInputRef.current?.focus();
      return;
    }
    const prev = (ticket.title || "").trim();
    if (next === prev) {
      setEditingTitle(false);
      setTitleError(null);
      return;
    }
    setTitleSaving(true);
    setTitleError(null);
    try {
      await onTitleChange(next);
      setEditingTitle(false);
    } catch (e) {
      setTitleError(e instanceof Error ? e.message : String(e));
      titleInputRef.current?.focus();
    } finally {
      setTitleSaving(false);
    }
  };

  return (
    <header className="support-ticket-detail__header">
      <div className="support-ticket-detail__numberrow">
        {onBackToList && (
          <button
            type="button"
            className="support-ticket-detail__back"
            onClick={onBackToList}
            title="一覧へ戻る"
          >
            ← 一覧
          </button>
        )}
        <span className="support-ticket-detail__number">{ticket.ticket_number}</span>
      </div>
      {editingTitle ? (
        <div className="support-ticket-detail__title-edit">
          <input
            ref={titleInputRef}
            className="support-ticket-detail__title-input"
            value={titleDraft}
            disabled={titleSaving}
            placeholder={displayTitle}
            aria-label="件名を編集"
            onChange={(e) => {
              setTitleDraft(e.target.value);
              if (titleError) setTitleError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void commitEditTitle();
              } else if (e.key === "Escape") {
                e.preventDefault();
                cancelEditTitle();
              }
            }}
            onBlur={() => {
              if (skipBlurSaveRef.current) {
                skipBlurSaveRef.current = false;
                return;
              }
              void commitEditTitle();
            }}
          />
          {titleError && <div className="support-ticket-detail__title-error">{titleError}</div>}
          <div className="support-ticket-detail__title-hint">Enter で保存 · Esc でキャンセル</div>
        </div>
      ) : (
        <div className="support-ticket-detail__title-row">
          <h3 className="support-ticket-detail__title">{displayTitle}</h3>
          {canWrite && (
            <button
              type="button"
              className="support-ticket-detail__title-edit-btn"
              title="件名を変更"
              aria-label="件名を変更"
              onClick={startEditTitle}
            >
              ✎
            </button>
          )}
        </div>
      )}
      {ticketViewers.length > 0 && (
        <div className="support-ticket-detail__viewers">
          他の担当者が対応中: {ticketViewers.join(", ")}（同時編集にご注意）
        </div>
      )}

      {/* 既定は1行サマリ。SLA と担当はトリアージの中核なので常時表示・常時操作可（改善 C） */}
      <div className="support-meta-summary">
        <span className="support-meta-summary__text" title={metaSummaryLine(ticket)}>
          {metaSummaryLine(ticket)}
        </span>
        {sla.level !== "none" && (
          <span
            className={slaBadgeClass(sla)}
            title={ticket.sla_due_at ? `初回応答の期限: ${new Date(ticket.sla_due_at).toLocaleString()}` : ""}
          >
            {sla.label}
          </span>
        )}
        <span className="support-meta-summary__assignee">
          担当{" "}
          {canWrite ? (
            <select
              className="support-meta-chip__select"
              value={ticket.assigned_to || ""}
              onChange={(e) => onAssigneeChange(e.target.value || null)}
            >
              <option value="">未割当</option>
              {mentionUsers.map((u) => (
                <option key={u.email} value={u.email}>{u.label || u.email}</option>
              ))}
            </select>
          ) : (
            <b>{ticket.assigned_to ? ticket.assigned_to.split("@")[0] : "未割当"}</b>
          )}
        </span>
        {!ticket.assigned_to && canWrite && onAssignSelf && (
          <button type="button" className="support-assign-self-btn" onClick={onAssignSelf} title="自分を担当にして対応を開始">
            自分が対応
          </button>
        )}
        <button
          type="button"
          className="support-meta-summary__toggle"
          onClick={() => setMetaOpen((v) => !v)}
          aria-expanded={metaOpen}
        >
          詳細 {metaOpen ? "▴" : "▾"}
        </button>
        {onRailToggle && (
          <button
            type="button"
            className={"support-rail-toggle" + (railOpen ? " support-rail-toggle--open" : "")}
            onClick={onRailToggle}
            aria-expanded={!!railOpen}
            title="タスク・連絡先・現場予定・起票内容・メール配送ログ"
          >
            ≣ 情報
            {hasOpenTasks && <span className="support-rail-toggle__dot" aria-label="未完了タスクあり" />}
          </button>
        )}
      <div className="support-ticket-detail__statusbar">
        {STATUS_FLOW.map((s) => {
          const isCurrent = ticket.status === s;
          const allowed = transitions[ticket.status];
          const disabled = !isCurrent && !!allowed && !allowed.includes(s);
          return (
            <button
              key={s}
              type="button"
              className={"support-status-btn" + (isCurrent ? " support-status-btn--current" : "")}
              onClick={() => !disabled && onStatusChange(s)}
              disabled={disabled}
              title={disabled ? `「${STATUS_LABEL[ticket.status] || ticket.status}」からは変更できません` : ""}
            >
              {STATUS_LABEL[s]}
            </button>
          );
        })}
        <div style={{ position: "relative" }} ref={moreActionsRef}>
          <button type="button" className="support-btn-outline" onClick={onMoreActionsToggle}>
            その他 ▾
          </button>
          {moreActionsOpen && (
            <div className="support-dropdown">
              <button type="button" className="support-dropdown__item" onClick={onTriage}>トリアージ</button>
              <button
                type="button"
                className="support-dropdown__item"
                onClick={onCorrection}
                disabled={!ticket.contact_email}
                style={!ticket.contact_email ? { color: "var(--text-disabled,#aeb2ae)", cursor: "not-allowed" } : undefined}
              >
                訂正メールを送る
              </button>
              {canWrite && onJunkClose && (
                <button
                  type="button"
                  className="support-dropdown__item"
                  onClick={onJunkClose}
                  disabled={ticket.status === "closed" && (ticket.tags || []).includes("迷惑")}
                  style={
                    ticket.status === "closed" && (ticket.tags || []).includes("迷惑")
                      ? { color: "var(--text-disabled,#aeb2ae)", cursor: "not-allowed" }
                      : undefined
                  }
                  title={
                    ticket.status === "closed" && (ticket.tags || []).includes("迷惑")
                      ? "既に迷惑メールとしてクローズ済みです"
                      : "closed + タグ「迷惑」。送信元もブロックします（顧客通知なし）"
                  }
                >
                  迷惑メールとしてクローズ
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      </div>

      {metaOpen && (
      <div className="support-meta-chips">
        {/* 区分はメール取込時に自動判定される。外れることがあるので manager 以上は直せる
            （BE 側 SUPPORT_CHANNEL_EDIT_ROLES と同じ範囲。表示制御だけで強制は BE）。 */}
        <span className="support-meta-chip">
          区分{" "}
          {canEditChannel ? (
            <select
              className="support-meta-chip__select"
              value={ticket.channel}
              onChange={(e) => onChannelChange(e.target.value as Channel)}
            >
              {channelSelectOptions(ticket.channel).map((c) => (
                <option key={c.key} value={c.key} disabled={c.key === "device_monitor"}>
                  {c.label}
                </option>
              ))}
            </select>
          ) : (
            <b>{CHANNELS.find((c) => c.key === ticket.channel)?.label || ticket.channel}</b>
          )}
        </span>
        {(ticket.tags || []).includes(CHANNEL_REVIEW_TAG) && (
          <span
            className="support-meta-chip"
            style={{ color: "var(--warning,#ffb300)", fontWeight: 700 }}
            title="起票時の自動判定が低確度でした。区分が正しいか確認してください。"
          >
            ⚠ {CHANNEL_REVIEW_TAG}
          </span>
        )}
        {ticket.category && (
          <span className="support-meta-chip">種別 <b>{CATEGORY_LABEL[ticket.category] || ticket.category}</b></span>
        )}
        {ticket.product && <span className="support-meta-chip">製品 <b>{ticket.product}</b></span>}
        {ticket.unit_serial && (
          <span className="support-meta-chip support-meta-chip--link">
            シリアル <span className="support-mono-id">{ticket.unit_serial}</span>
            <button type="button" className="support-meta-chip__btn" onClick={() => onGoToDevice(ticket.unit_serial!)}>
              dash →
            </button>
          </span>
        )}
        {ticket.device_mac && (
          <span className="support-meta-chip support-meta-chip--link">
            MAC <span className="support-mono-id">{ticket.device_mac}</span>
            <button type="button" className="support-meta-chip__btn" onClick={() => openDashMac(ticket.device_mac!)}>
              dash →
            </button>
          </span>
        )}
        {ticket.company && <span className="support-meta-chip">会社 <b>{ticket.company}</b></span>}
        {ticket.channel === "partner" && ticket.partner_code && (
          <span className="support-meta-chip">取引先 <b>{ticket.partner_code}</b></span>
        )}
        {ticket.channel === "partner" && ticket.case_no && (
          <span className="support-meta-chip">案件 <b>{ticket.case_no}</b></span>
        )}
        {ticket.install_date && <span className="support-meta-chip">設置日 <b>{ticket.install_date}</b></span>}
        {ticket.channel === "customer" && ticket.purchase_route && (
          <span className="support-meta-chip">購入経路 <b>{ticket.purchase_route}</b></span>
        )}
        {(ticket.channels || []).length > 0 && (
          <span className="support-meta-chip">
            通知先 <b>{(ticket.channels || []).map((ch) => CH_LABEL[ch] || ch).join(" / ")}</b>
          </span>
        )}
        {ticket.csat_score != null && <span className="support-meta-chip">満足度 <b>⭐{ticket.csat_score}/5</b></span>}
        {ticket.sentiment && <span className="support-meta-chip">感情 <b>{ticket.sentiment}</b></span>}
        {(ticket.open_task_count ?? 0) > 0 && (
          <span className="support-meta-chip">タスク <b>{ticket.open_task_count}</b></span>
        )}
      </div>
      )}

    </header>
  );
}
